import { ChangeEvent, ElementType, useEffect, useRef } from "react"
import { Grid, Hidden, TextField } from "@mui/material"
import dayjs, { Dayjs } from "dayjs"
import { DateField } from '@mui/x-date-pickers/DateField'
import MaskedTextField, { MaskedTextFieldProps } from "../../../MaskedTextField"
import { ISettingsItem } from "../../../../Settings"
import { useForm } from "../../../../Form"
import { useTranslation } from "../../../../i18n"
import { ChoosePlanText } from '../../..'

type FieldOnChange =
  | ((event: ChangeEvent<HTMLInputElement>) => void)
  | ((value: Dayjs | null) => void)

interface fieldProps extends Omit<MaskedTextFieldProps, "mask" | "value" | "onChange"> {
  Component: ElementType
  value: string | Dayjs | null
  onChange?: FieldOnChange
  mask?: string | string[]
  clearable?: boolean
  format?: string
  md?: number
  autoComplete?: string
}

export default function PersonalInfo({ name, variables }: ISettingsItem) {
  const { currentFields, currentFields: { fieldErrors }, formState: { showError }, setCurrentFields, setModelledField, setFormState } = useForm()
  const { t } = useTranslation()
  const { cpfCnpj, date } = currentFields
  const fieldsUnfilled = useRef([] as string[])
  const defaultTextArray = [
    t.page2.personalInfo.title,
    t.page2.personalInfo.subtitle,
    t.page2.personalInfo.fullName,
    t.page2.personalInfo.cpfCnpj,
    t.page2.personalInfo.birthdate,
  ]
  const { text = defaultTextArray } = variables || {}

  const fields: fieldProps[] = [
    {
      Component: TextField,
      required: true,
      error: false,
      helperText: t.common.requiredField,
      name: "name",
      id: "outlined-name-input",
      value: currentFields.name,
      onChange: setCurrentFields,
      label: text[2],
      type: "name",
    },
    {
      Component: MaskedTextField,
      required: true,
      error: false,
      helperText: t.common.requiredField,
      name: "cpfCnpj",
      id: "outlined-cpf-cnpj-input",
      value: cpfCnpj,
      onChange: setCurrentFields,
      label: text[3],
      type: "tel",
      mask: ["###.###.###-##", "##.###.###/####-##"],
      md: 6,
      autoComplete: "off"
    },
    {
      Component: DateField,
      required: cpfCnpj.length === 18 ? false : true,
      helperText: t.common.requiredField,
      name: "date",
      id: "outlined-date-input",
      value: date,
      onChange: (value: Dayjs | null) => {
        if (value && dayjs.isDayjs(value) && value.isValid()) {
          setModelledField("date", value)
        } else {
          setModelledField("date", null)
        }
      },
      label: text[4],
      clearable: true,
      format: "DD/MM/YYYY",
      md: 6,
    }
  ]

  const renderInputs = ({
    Component,
    required,
    helperText,
    name,
    id,
    value,
    onChange,
    label,
    type,
    mask,
    clearable,
    format,
    autoComplete
  }: fieldProps, index: number, mob: boolean) => {
    // Erro vindo do 422 da API tem precedência sobre o "campo obrigatório"
    // local: é mais específico e explica o motivo real da recusa.
    const erroDoCampo = fieldErrors?.[name]?.[0]

    const safeValue = name === "date"
    ? (dayjs.isDayjs(value) ? value : value ? dayjs(value) : null)
    : value

    return <Component
      key={index}
      required={required}
      error={Boolean(erroDoCampo) || (showError && fieldsUnfilled.current.includes(name) && required)}
      helperText={erroDoCampo || (showError && fieldsUnfilled.current.includes(name) && helperText)}
      name={name}
      id={id}
      value={safeValue}
      onChange={onChange}
      fullWidth
      label={label}
      type={type}
      mask={mask}
      clearable={clearable}
      format={format}
      size={mob ? 'medium' : 'small'}
      autoComplete={autoComplete}
    />
  }

  useEffect(() => {
    fieldsUnfilled.current = fields
      .filter(field => field.required)
      .reduce(
        (agg: string[], { name, value }) => {
          let processedValue: string | null | undefined

          if (name === "date") {
            // Converte Dayjs para string antes de passar ao dayjs()
            const rawDate = dayjs.isDayjs(value)
              ? value.format('YYYY-MM-DD')
              : (value as string | null)

            processedValue = dayjs(rawDate).isValid()
              ? dayjs(rawDate).format('YYYY-MM-DD')
              : "Invalid Date"
          } else {
            processedValue = value as string | null
          }

          if (["", "null", null, undefined, "Invalid Date"].includes(processedValue)) {
            agg.push(name)
          }

          return agg
        }, []
      )

    setFormState('form1', fieldsUnfilled.current.length === 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFields.name, cpfCnpj, date])

  const beforeFracture = fields.filter(field => ['name'].includes(field.name))
  const fracture = fields.filter(field => ['cpfCnpj', 'date'].includes(field.name))

  return (
    <Grid container item className={name ? `${name} personal-info form` : "personal-info form"} xs={12} sx={{ display: 'flex', gap: '20px' }}>
      <Grid item className="textArea">
        <ChoosePlanText name="personal-info-text" variables={{ text }} />
      </Grid>
      <Hidden mdUp>
        <Grid container item className="formArea" xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {fields.map((field, index) => renderInputs(field, index, true))}
        </Grid>
      </Hidden>
      <Hidden only={['xs', 'xl']}>
        <Grid container item className="formArea" xs={12}>
          {beforeFracture.map((field, index) => renderInputs(field, index, false))}
          <Grid item className="fractArea">
            {fracture.map((field, index) => <Grid key={index} item md={field.md}>{renderInputs(field, index, false)}</Grid>)}
          </Grid>
        </Grid>
      </Hidden>
      <Hidden xlDown>
        <Grid container item className="formArea" xs={12}>
          {beforeFracture.map((field, index) => renderInputs(field, index, true))}
          <Grid item className="fractArea">
            {fracture.map((field, index) => <Grid key={index} item md={field.md}>{renderInputs(field, index, true)}</Grid>)}
          </Grid>
        </Grid>
      </Hidden>
    </Grid>
  )
}